import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon } from "../../assets/Icons/ArrowLeft";
import { ArrowRightIcon } from "../../assets/Icons/ArrowRightIcon";
import { Table2ColumnsIcon } from "../../assets/Icons/Table2ColumnsIcon";
import { FilesService } from "../../api/services/files.service";
import "./PreviewFileData.scss";

const PREVIEW_ERROR_FALLBACK = "Somethiing went wrong Please try again later";

const buildRowOptions = (count = 7) =>
  Array.from({ length: Math.max(1, count) }, (_, index) => `Row ${index + 1}`);

const buildColumnOptions = () =>
  Array.from({ length: 7 }, (_, index) => `Column ${index + 1}`);

const toRows = (value) => {
  if (!Array.isArray(value)) return [];
  if (value.every((row) => Array.isArray(row))) return value;

  const keys = Array.from(
    new Set(value.flatMap((row) => Object.keys(row || {}))),
  );
  return [keys, ...value.map((row) => keys.map((key) => row?.[key] ?? ""))];
};

const columnNameToIndex = (columnName) =>
  columnName.split("").reduce(
    (index, character) => index * 26 + character.charCodeAt(0) - 64,
    0,
  ) - 1;

const previewCellsToRows = (previewCells) => {
  const cellEntries = Object.entries(previewCells || {})
    .map(([address, value]) => {
      const match = address.match(/^([A-Z]+)(\d+)$/i);
      if (!match) return null;
      return {
        rowIndex: Number(match[2]) - 1,
        columnIndex: columnNameToIndex(match[1].toUpperCase()),
        value,
      };
    })
    .filter(Boolean);

  if (cellEntries.length === 0) return [];

  const maxRowIndex = Math.max(
    ...cellEntries.map((entry) => entry.rowIndex),
  );
  const maxColumnIndex = Math.max(
    ...cellEntries.map((entry) => entry.columnIndex),
  );
  const rows = Array.from({ length: maxRowIndex + 1 }, () =>
    Array(maxColumnIndex + 1).fill(""),
  );

  cellEntries.forEach(({ rowIndex, columnIndex, value }) => {
    rows[rowIndex][columnIndex] = value ?? "";
  });

  return rows.map((row, index) => [`Row ${index + 1}`, ...row]);
};

const normalizePreviewPayload = (payload) => {
  const source = payload?.data ?? payload;
  const sheets = source?.sheets;
  if (sheets && typeof sheets === "object" && !Array.isArray(sheets)) {
    const entries = Object.entries(sheets)
      .map(([name, rows]) => [name, toRows(rows)])
      .filter(([, rows]) => rows.length > 0);
    if (entries.length > 0) return entries;
  }

  const previewData = source?.PreviewData || source?.previewData;
  if (previewData && typeof previewData === "object") {
    const previewSheetNames = Object.keys(previewData);
    const entries = previewSheetNames
      .map((name) => [name, previewCellsToRows(previewData[name])])
      .filter(([, rows]) => rows.length > 0);
    if (entries.length > 0) return entries;
  }

  const rows = toRows(source?.rows || source?.data || source);
  return rows.length > 0 ? [["Sheet 1", rows]] : [];
};

const normalizeSheetName = (name) => String(name || "").replace(/\s+/g, "").toLowerCase();

const getPreviewSheetConfigurations = (payload) => {
  const source = payload?.data ?? payload;
  const sheets = source?.file?.sheets ?? source?.sheets;

  if (!Array.isArray(sheets)) return [];

  return sheets.map((sheet) => ({
    ...sheet,
    sheetName: sheet.sheetName || `Sheet ${sheet.sheetIndex || 1}`,
    dataTriageEnabled: Boolean(sheet.dataTriageEnabled),
    included: Boolean(sheet.dataTriageEnabled),
    headerRow: Number(sheet.headerRow) || 1,
    headerColumn: Number(sheet.headerColumn) || 1,
    dataStartRow: Number(sheet.dataStartRow) || 2,
    dataStartColumn: Number(sheet.dataStartColumn) || 1,
  }));
};

const toRowOption = (value) => `Row ${Number(value) || 1}`;
const toColumnOption = (value) => `Column ${Number(value) || 1}`;
const fromOptionNumber = (value) => Number(String(value).match(/\d+/)?.[0]) || 1;

const getErrorMessage = (payload) => {
  const message =
    payload?.message ||
    payload?.errorMessage ||
    payload?.error?.message ||
    payload?.error ||
    payload?.detail ||
    payload?.title ||
    (Array.isArray(payload?.errors) ? payload.errors.join(" ") : payload?.errors);
  return typeof message === "string" && message.trim()
    ? message.trim()
    : PREVIEW_ERROR_FALLBACK;
};

const getPreviewBlobNames = (xmlText, previewFileName) => {
  const xml = new DOMParser().parseFromString(xmlText, "application/xml");
  const escapedName = previewFileName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const matchingFileName = new RegExp(`^${escapedName}(?:_.+)?\\.json$`, "i");

  return Array.from(xml.getElementsByTagName("Blob"))
    .map((blob) => blob.getElementsByTagName("Name")[0]?.textContent)
    .filter(Boolean)
    .filter((blobName) => matchingFileName.test(blobName.split("/").pop()));
};

const getSheetLabelFromBlobName = (blobName, previewFileName) => {
  const blobFileName = blobName.split("/").pop();
  const suffix = blobFileName.slice(previewFileName.length, -".json".length);
  if (!suffix.startsWith("_")) return "";

  const label = suffix.slice(1);
  const numberedSheet = label.match(/^Sheet\s*(\d+)$/i);
  return numberedSheet ? `Sheet ${numberedSheet[1]}` : label;
};

const listPreviewBlobs = async (containerUrl, sasQuery, folderPath, previewFileName) => {
  const matchingBlobNames = [];
  let marker = "";

  do {
    const query = [
      sasQuery,
      "restype=container",
      "comp=list",
      `prefix=${encodeURIComponent(folderPath)}`,
      marker ? `marker=${encodeURIComponent(marker)}` : "",
    ].filter(Boolean).join("&");
    const response = await fetch(`${containerUrl}?${query}`);
    if (!response.ok) {
      throw new Error(`Failed to list preview files: ${response.status}`);
    }

    const xmlText = await response.text();
    matchingBlobNames.push(...getPreviewBlobNames(xmlText, previewFileName));
    const xml = new DOMParser().parseFromString(xmlText, "application/xml");
    marker = xml.getElementsByTagName("NextMarker")[0]?.textContent || "";
  } while (marker);

  return matchingBlobNames.sort((left, right) => {
    const baseName = `${previewFileName}.json`.toLowerCase();
    const leftIsBaseFile = left.split("/").pop().toLowerCase() === baseName;
    const rightIsBaseFile = right.split("/").pop().toLowerCase() === baseName;
    if (leftIsBaseFile !== rightIsBaseFile) return leftIsBaseFile ? -1 : 1;
    return left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
  });
};

export const fetchPreviewSheets = async ({
  actualOrderId,
  financialYear,
  fileType,
  sourceFileName,
}) => {
  const previewFileName = sourceFileName.replace(/\.[^.]+$/, "");
  const { sasUrl } = await FilesService.getSasToken();
  const sasUrlParts = String(sasUrl || "").split("?");
  const containerUrl = sasUrlParts.shift();
  const sasQuery = sasUrlParts.join("?");
  if (!containerUrl || !sasQuery) throw new Error("SAS URL is unavailable");

  const blobFolderPath = [
    "DataTriage_Processing",
    actualOrderId,
    financialYear,
    fileType,
  ].join("/");
  const blobNames = await listPreviewBlobs(
    containerUrl,
    sasQuery,
    blobFolderPath,
    previewFileName,
  );
  const jsonBlobNames = blobNames.length > 0
    ? blobNames
    : [`${blobFolderPath}/${previewFileName}.json`];

  const loadedSheets = await Promise.all(jsonBlobNames.map(async (blobName) => {
    const blobPath = blobName.split("/").map(encodeURIComponent).join("/");
    const response = await fetch(`${containerUrl}/${blobPath}?${sasQuery}`);
    if (!response.ok) {
      const responseText = await response.text().catch(() => "");
      let errorPayload = responseText;
      try {
        errorPayload = responseText ? JSON.parse(responseText) : null;
      } catch {
        errorPayload = { message: responseText };
      }
      throw new Error(getErrorMessage(errorPayload));
    }

    const payload = await response.json();
    const normalizedSheets = normalizePreviewPayload(payload);
    const fileSheetLabel = getSheetLabelFromBlobName(blobName, previewFileName);
    return normalizedSheets.map(([name, rows]) => [
      normalizedSheets.length === 1 && fileSheetLabel && name === "Sheet 1"
        ? fileSheetLabel
        : name,
      rows,
    ]);
  }));

  const sheetsByName = new Map();
  loadedSheets.flat().forEach(([name, rows]) => {
    const normalizedName = name.replace(/\s+/g, "").toLowerCase();
    if (!sheetsByName.has(normalizedName)) {
      sheetsByName.set(normalizedName, [name, rows]);
    }
  });
  return Array.from(sheetsByName.values());
};

export const PreviewFileData = ({ fileDetails }) => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const user = useSelector((state) => state.user?.user);
  const savedBy = user?.email || user?.mail || user?.username || "";
  const [activeSheet, setActiveSheet] = useState("");
  const [isSheetMenuOpen, setIsSheetMenuOpen] = useState(false);
  const [sheetConfigurations, setSheetConfigurations] = useState([]);
  const initialSheetConfigurationsRef = useRef([]);
  const [previewSheets, setPreviewSheets] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [isSavingPreview, setIsSavingPreview] = useState(false);
  const [savePreviewError, setSavePreviewError] = useState("");
  const sourceFileName = fileDetails?.name || fileDetails?.fileName || "";
  const fileName = sourceFileName || "No file selected";
  const isReadOnlyFile = /^Consolidated_EY/i.test(
    sourceFileName.split(/[\\/]/).pop()?.trim() || "",
  );
  const fileId = fileDetails?.fileUploadId || fileDetails?.fileId || fileDetails?.id;
  const fileType = fileDetails?.type || fileDetails?.fileType || "JE";
  const actualOrderId = fileDetails?.actualOrderId || fileDetails?.orderId || orderId;
  const financialYear = fileDetails?.period || fileDetails?.financialYear || "CY";

  useEffect(() => {
    let isActive = true;

    const fetchPreviewData = async () => {
      if (!actualOrderId || !sourceFileName) {
        setPreviewSheets([]);
        setPreviewError(PREVIEW_ERROR_FALLBACK);
        return;
      }

      try {
        setIsLoadingPreview(true);
        setPreviewError("");
        setPreviewSheets([]);
        setActiveSheet("");

        // Fetch sheet configuration alongside the existing blob preview data.
        const previewConfigurationRequest = fileId
          ? FilesService.getPreview(actualOrderId, fileId)
              .then(getPreviewSheetConfigurations)
              .catch((error) => {
                console.error("Failed to fetch preview API data:", error);
                return [];
              })
          : Promise.resolve([]);

        const nextSheets = await fetchPreviewSheets({
          actualOrderId,
          financialYear,
          fileType,
          sourceFileName,
        });
        const nextSheetConfigurations = await previewConfigurationRequest;

        if (nextSheets.length === 0) {
          throw new Error(PREVIEW_ERROR_FALLBACK);
        }

        if (isActive) {
          setPreviewSheets(nextSheets);
          const availableSheetNames = nextSheetConfigurations.length
            ? nextSheetConfigurations.map((sheet) => sheet.sheetName)
            : nextSheets.map(([name]) => name);
          const firstEnabledSheet =
            nextSheetConfigurations.find(
              (sheet) => sheet.isSelected && sheet.dataTriageEnabled,
            )?.sheetName ||
            nextSheetConfigurations.find((sheet) => sheet.dataTriageEnabled)
              ?.sheetName;
          const loadedSheetConfigurations = nextSheetConfigurations.length
            ? nextSheetConfigurations
            : nextSheets.map(([sheetName], index) => ({
                  sheetName,
                  sheetIndex: index + 1,
                  isSelected: index === 0,
                  dataTriageEnabled: true,
                  included: true,
                  headerRow: 1,
                  headerColumn: 1,
                  dataStartRow: 2,
                  dataStartColumn: 1,
                }));
          initialSheetConfigurationsRef.current = loadedSheetConfigurations.map(
            (sheet) => ({
              sheetName: normalizeSheetName(sheet.sheetName),
              dataTriageEnabled: Boolean(sheet.dataTriageEnabled),
              headerRow: Number(sheet.headerRow) || 1,
              headerColumn: Number(sheet.headerColumn) || 1,
              dataStartRow: Number(sheet.dataStartRow) || 2,
              dataStartColumn: Number(sheet.dataStartColumn) || 1,
            }),
          );
          setSheetConfigurations(loadedSheetConfigurations);
          setActiveSheet(
            firstEnabledSheet || availableSheetNames[0] || nextSheets[0]?.[0] || "",
          );
        }
      } catch (error) {
        console.error("Failed to fetch preview file data:", error);
        if (isActive) {
          setPreviewSheets([]);
          setPreviewError(error?.message || PREVIEW_ERROR_FALLBACK);
        }
      } finally {
        if (isActive) setIsLoadingPreview(false);
      }
    };

    fetchPreviewData();
    return () => {
      isActive = false;
    };
  }, [actualOrderId, sourceFileName, fileId, fileType, financialYear]);

  const sheetNames = useMemo(() => {
    if (sheetConfigurations.length > 0) {
      return sheetConfigurations.map((sheet) => sheet.sheetName);
    }
    return previewSheets.map(([name]) => name);
  }, [previewSheets, sheetConfigurations]);
  const includedSheetCount = sheetConfigurations.length > 0
    ? sheetConfigurations.filter((sheet) => sheet.included).length
    : sheetNames.length;
  const activeSheetConfiguration = sheetConfigurations.find(
    (sheet) => normalizeSheetName(sheet.sheetName) === normalizeSheetName(activeSheet),
  );
  const activeRows =
    previewSheets.find(
      ([name]) => normalizeSheetName(name) === normalizeSheetName(activeSheet),
    )?.[1] || [];
  const previewColumns = activeRows[0]?.length || 0;
  const columnOptions = Array.from(
    { length: Math.max(0, previewColumns - 1) },
    (_, index) => `Column ${index + 1}`,
  );
  const selectedHeaderRow = toRowOption(activeSheetConfiguration?.headerRow);
  const selectedHeaderColumn = toColumnOption(activeSheetConfiguration?.headerColumn);
  const dataStartRow = toRowOption(activeSheetConfiguration?.dataStartRow || 2);
  const dataStartColumn = toColumnOption(activeSheetConfiguration?.dataStartColumn);
  const updateActiveSheetConfiguration = (field, value) => {
    setSheetConfigurations((currentConfigurations) =>
      currentConfigurations.map((sheet) =>
        normalizeSheetName(sheet.sheetName) === normalizeSheetName(activeSheet)
          ? { ...sheet, [field]: fromOptionNumber(value) }
          : sheet,
      ),
    );
  };
  const updateSheetIncluded = (sheetName, included) => {
    setSheetConfigurations((currentConfigurations) =>
      currentConfigurations.map((sheet) =>
        normalizeSheetName(sheet.sheetName) === normalizeSheetName(sheetName)
          ? { ...sheet, included, dataTriageEnabled: included }
          : sheet,
      ),
    );
  };
  const canSelectSheet = (sheetName) => {
    if (sheetConfigurations.length === 0) return true;
    return sheetConfigurations.find(
      (sheet) => normalizeSheetName(sheet.sheetName) === normalizeSheetName(sheetName),
    )?.dataTriageEnabled === true;
  };

  const getHeadersAndFirstRowForSheet = (sheetConfiguration) => {
    const sheetRows = previewSheets.find(
      ([name]) =>
        normalizeSheetName(name) ===
        normalizeSheetName(sheetConfiguration.sheetName),
    )?.[1] || [];
    const headerRowIndex = Math.max(0, Number(sheetConfiguration.headerRow) - 1);
    const headerRow = sheetRows[headerRowIndex] || [];
    const dataRowIndex = Math.max(0, Number(sheetConfiguration.dataStartRow) - 1);
    const dataRow = sheetRows[dataRowIndex] || [];
    const hasRowLabel = sheetRows.some((row) =>
      /^row\s*\d+$/i.test(String(row?.[0] || "")),
    );
    const firstDataColumnIndex = Math.max(
      0,
      Number(sheetConfiguration.headerColumn) - 1 + (hasRowLabel ? 1 : 0),
    );
    const lastColumnIndex = Math.max(
      firstDataColumnIndex,
      ...sheetRows.map((row) => row.length - 1),
    );
    let blankHeaderCount = 0;

    const headers = Array.from(
      { length: lastColumnIndex - firstDataColumnIndex + 1 },
      (_, offset) => {
        const columnIndex = firstDataColumnIndex + offset;
        let header = String(headerRow[columnIndex] ?? "").trim();

        for (
          let rowIndex = headerRowIndex - 1;
          !header && rowIndex >= 0;
          rowIndex -= 1
        ) {
          header = String(sheetRows[rowIndex]?.[columnIndex] ?? "").trim();
        }

        if (!header) {
          blankHeaderCount += 1;
          header = `Blank_${blankHeaderCount}`;
        }

        return header;
      },
    );

    // Repeated names (case-insensitive) are numbered name_1, name_2, ...
    const totals = {};
    headers.forEach((header) => {
      const key = header.toLowerCase();
      totals[key] = (totals[key] || 0) + 1;
    });
    const seen = {};
    const normalizedHeaders = headers.map((header) => {
      const key = header.toLowerCase();
      if (totals[key] < 2) return header;
      seen[key] = (seen[key] || 0) + 1;
      return `${header}_${seen[key]}`;
    });

    const firstRow = normalizedHeaders.reduce((rowValues, header, offset) => {
      const value = dataRow[firstDataColumnIndex + offset];
      rowValues[header] = value === null || value === undefined ? "" : String(value);
      return rowValues;
    }, {});

    return { headers: normalizedHeaders, firstRow };
  };

  const handleSaveAndContinue = async () => {
    if (isReadOnlyFile) {
      return;
    }

    if (!actualOrderId || !fileId || !activeSheet) {
      setSavePreviewError("Unable to save preview: order, file, or selected sheet is missing.");
      return;
    }

    const currentConfigurationSnapshot = sheetConfigurations.map((sheet) => ({
      sheetName: normalizeSheetName(sheet.sheetName),
      dataTriageEnabled: Boolean(sheet.included),
      headerRow: Number(sheet.headerRow) || 1,
      headerColumn: Number(sheet.headerColumn) || 1,
      dataStartRow: Number(sheet.dataStartRow) || 2,
      dataStartColumn: Number(sheet.dataStartColumn) || 1,
    }));
    const initialConfigurationSnapshot = initialSheetConfigurationsRef.current;
    const hasConfigurationChanges =
      JSON.stringify(currentConfigurationSnapshot) !==
      JSON.stringify(initialConfigurationSnapshot);

    if (!hasConfigurationChanges) {
      navigate(`/orders/${orderId}`, {
        state: { returnToFiles: true },
      });
      return;
    }

    const payload = {
      savedBy,
      sheets: sheetConfigurations.map((sheet, index) => {
        const { headers, firstRow } = getHeadersAndFirstRowForSheet(sheet);

        return {
          sheetName: sheet.sheetName,
          sheetIndex: Number(sheet.sheetIndex) || index + 1,
          isSelected:
            normalizeSheetName(sheet.sheetName) === normalizeSheetName(activeSheet),
          dataTriageEnabled: Boolean(sheet.included),
          headerRow: Number(sheet.headerRow) || 1,
          headerColumn: Number(sheet.headerColumn) || 1,
          dataStartRow: Number(sheet.dataStartRow) || 2,
          dataStartColumn: Number(sheet.dataStartColumn) || 1,
          headers,
          firstRow,
          year: financialYear,
        };
      }),
    };

    try {
      setIsSavingPreview(true);
      setSavePreviewError("");
      await FilesService.savePreview(actualOrderId, fileId, payload);
      navigate(`/orders/${orderId}`, {
        state: { returnToFiles: true, refreshFiles: true },
      });
    } catch (error) {
      console.error("Failed to save preview configuration:", error);
      setSavePreviewError(error?.message || PREVIEW_ERROR_FALLBACK);
    } finally {
      setIsSavingPreview(false);
    }
  };

  const handleBack = () =>
    navigate(`/orders/${orderId}`, {
      state: { returnToFiles: true, returnFromPreview: true },
    });

  return (
    <main className="preview-file-data-page">
      <header className="preview-file-data-header">
        <button type="button" className="preview-file-back-btn" onClick={handleBack}>
          <ArrowLeftIcon width={14} height={14} color="#A4A3B1" />
          Back
        </button>
        <h1>File Preview &amp; Configuration</h1>
      </header>

      <div className="preview-file-workspace">
        <aside className="preview-file-sidebar" aria-label="File configuration">
          <div className="preview-file-identity">
            <span className="preview-file-type-badge">{fileType}</span>
            <span title={fileName}>{fileName}</span>
          </div>

          <div className="preview-config-field">
            <span>Sheet/Tab</span>
            <div className="preview-sheet-picker">
              <button
                type="button"
                className="preview-sheet-picker-trigger"
                aria-label="Select sheet or tab"
                aria-controls="preview-sheet-picker-menu"
                aria-expanded={isSheetMenuOpen}
                onClick={() => setIsSheetMenuOpen((isOpen) => !isOpen)}
                disabled={isReadOnlyFile || sheetNames.length === 0}
              >
                <span>{activeSheet || "No sheets available"}</span>
                <span className="preview-sheet-picker-chevron" aria-hidden="true">
                  {isSheetMenuOpen ? "⌃" : "⌄"}
                </span>
              </button>
              {isSheetMenuOpen && sheetNames.length > 0 && (
                <div
                  className="preview-sheet-picker-menu"
                  id="preview-sheet-picker-menu"
                  role="group"
                  aria-label="Available sheets"
                >
                  {sheetNames.map((sheet) => (
                    <div
                      className={`preview-sheet-picker-option${activeSheet === sheet ? " is-active" : ""}`}
                      key={sheet}
                    >
                      <label className="preview-sheet-picker-radio">
                        <input
                          type="radio"
                          name="preview-sheet-selection"
                          value={sheet}
                          checked={activeSheet === sheet}
                          disabled={isReadOnlyFile || !canSelectSheet(sheet)}
                          onChange={() => setActiveSheet(sheet)}
                        />
                        <span className="preview-sheet-radio-indicator" aria-hidden="true" />
                        <span>{sheet}</span>
                        {activeSheet === sheet && (
                          <span className="preview-sheet-picker-check" aria-hidden="true">
                            ✓
                          </span>
                        )}
                      </label>
                      <label className="preview-sheet-include-toggle">
                        <input
                          type="checkbox"
                          aria-label={`Include ${sheet} for mapping`}
                          checked={sheetConfigurations.find(
                            (configuration) =>
                              normalizeSheetName(configuration.sheetName) ===
                              normalizeSheetName(sheet),
                          )?.included ?? true}
                            disabled={isReadOnlyFile}
                          onChange={(event) =>
                            updateSheetIncluded(sheet, event.target.checked)
                          }
                        />
                        <span aria-hidden="true" />
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <small>
              {includedSheetCount} of {sheetNames.length} sheets included for mapping
            </small>
          </div>

          <label className="preview-config-field">
            <span>Header Row</span>
            <select
              value={activeRows.length ? selectedHeaderRow : ""}
              onChange={(event) =>
                updateActiveSheetConfiguration("headerRow", event.target.value)
              }
              disabled={isReadOnlyFile || !activeRows.length}
            >
              {!activeRows.length && <option value="">No rows available</option>}
              {buildRowOptions(activeRows.length).map((row) => <option key={row}>{row}</option>)}
            </select>
            <small>The row containing column names</small>
          </label>

          <label className="preview-config-field">
            <span>Header Column</span>
            <select
              value={activeRows.length ? selectedHeaderColumn : ""}
              onChange={(event) =>
                updateActiveSheetConfiguration("headerColumn", event.target.value)
              }
              disabled={isReadOnlyFile || !columnOptions.length}
            >
              {!columnOptions.length && <option value="">No columns available</option>}
              {buildColumnOptions().map((column) => <option key={column}>{column}</option>)}
            </select>
            <small>The column containing headers</small>
          </label>

          <label className="preview-config-field">
            <span>Data start Row</span>
            <select
              value={activeRows.length ? dataStartRow : ""}
              onChange={(event) =>
                updateActiveSheetConfiguration("dataStartRow", event.target.value)
              }
              disabled={isReadOnlyFile || !activeRows.length}
            >
              {!activeRows.length && <option value="">No rows available</option>}
              {buildRowOptions(activeRows.length).map((row) => <option key={row}>{row}</option>)}
            </select>
            <small>The first row of actual data</small>
          </label>

          <label className="preview-config-field">
            <span>Data start Column</span>
            <select
              value={activeRows.length ? dataStartColumn : ""}
              onChange={(event) =>
                updateActiveSheetConfiguration("dataStartColumn", event.target.value)
              }
              disabled={isReadOnlyFile || !columnOptions.length}
            >
              {!columnOptions.length && <option value="">No columns available</option>}
              {buildColumnOptions().map((column) => <option key={column}>{column}</option>)}
            </select>
            <small>Columns before this are ignored</small>
          </label>
        </aside>

        <section className="preview-data-section" aria-labelledby="data-preview-title">
          <div className="preview-data-heading">
            <h2 id="data-preview-title">
              <Table2ColumnsIcon width={18} height={18} color="#A4A3B1" />
              Data Preview
            </h2>
            <div className="preview-header-indicator" aria-label={`Selected header row: ${selectedHeaderRow}`}>
              <span className="preview-header-indicator-swatch" />
              Header Row: {selectedHeaderRow}
            </div>
          </div>

          <div className="preview-sheet-tabs" role="tablist" aria-label="Workbook sheets">
            {sheetNames.map((sheet) => (
              <button
                key={sheet}
                type="button"
                role="tab"
                aria-selected={activeSheet === sheet}
                className={activeSheet === sheet ? "active" : ""}
                onClick={() => setActiveSheet(sheet)}
              >
                {sheet}
              </button>
            ))}
          </div>

          <div className="preview-grid-scroll">
            {isLoadingPreview ? (
              <div className="preview-data-state" role="status" aria-live="polite">
                <span className="preview-data-spinner" />
                Loading preview data...
              </div>
            ) : previewError ? (
              <div className="preview-data-state preview-data-state--error" role="alert">
                {previewError}
              </div>
            ) : activeRows.length === 0 ? (
              <div className="preview-data-state">No preview data available.</div>
            ) : (
            <table className="preview-data-grid">
              <thead>
                <tr>
                  <th>Row</th>
                  {columnOptions.map((column) => (
                    <th key={column}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activeRows.map((row, rowIndex) => (
                  <tr
                    key={`${row[0]}-${rowIndex}`}
                    className={`${row[0] === selectedHeaderRow ? "preview-header-row" : ""} ${row[0] === dataStartRow ? "preview-data-start-row" : ""}`}
                  >
                    {row.map((cell, index) => (
                      <td key={`${rowIndex}-${index}`} title={String(cell ?? "")}>{String(cell ?? "")}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        </section>
      </div>

      <footer className="preview-file-footer">
        {savePreviewError && <span className="preview-save-error" role="alert">{savePreviewError}</span>}
        <button
          type="button"
          className="preview-save-btn"
          onClick={handleSaveAndContinue}
          disabled={
            isReadOnlyFile ||
            isSavingPreview ||
            isLoadingPreview ||
            !fileId ||
            !activeSheet
          }
        >
          {isSavingPreview ? "Saving..." : "Save & Continue"}
          <ArrowRightIcon width={14} height={14} color="#FFFFFF" />
        </button>
      </footer>
    </main>
  );
};

export const PreviewFileDataRoute = () => {
  const location = useLocation();
  return (
    <PreviewFileData
      fileDetails={{
        ...location.state?.fileDetails,
        actualOrderId: location.state?.actualOrderId,
      }}
    />
  );
};

export default PreviewFileData;